import type { AttributeField, NodeField } from "@/features/properties/types/propertyAttributes";
import { getSurveyChildren, getSurveyLeafRawValue, isScalarSurveyLeaf } from "@/shared/lib/surveyFieldValue";

/**
 * Find all leaf field values under a specific node title.
 * Returns a record of { cleanTitle → value }.
 */
export function getNodeFieldValues(
  fields: AttributeField[],
  nodeKeywords: string[]
): Record<string, string | number | boolean | null> {
  const result: Record<string, string | number | boolean | null> = {};

  function collectLeafs(nodeFields: AttributeField[]) {
    for (const field of nodeFields) {
      const children = getSurveyChildren(field);
      if (children) {
        collectLeafs(children as AttributeField[]);
      } else if (isScalarSurveyLeaf(field)) {
        const cleanTitle = field.title.replace(/\s*\[.*?\]\s*$/, "").trim();
        result[cleanTitle] = getSurveyLeafRawValue(field);
      }
    }
  }

  // Pass 1: Check top-level nodes for direct matches first
  for (const field of fields) {
    if (field.type === "node") {
      const titleLower = field.title.toLowerCase();
      const matches = nodeKeywords.some((kw) => titleLower.includes(kw.toLowerCase()));
      if (matches) {
        collectLeafs((field as NodeField).fields);
        return result;
      }
    }
  }

  // Pass 2: Deep search nested nodes only if no top-level match
  for (const field of fields) {
    if (field.type === "node") {
      const childResult = getNodeFieldValues((field as NodeField).fields, nodeKeywords);
      if (Object.keys(childResult).length > 0) return childResult;
    }
  }

  return result;
}

/**
 * Search for a specific leaf field value by title keywords,
 * traversing the entire tree.
 */
export function findFieldValue(
  fields: AttributeField[],
  keywords: string[]
): string | number | boolean | null {
  for (const field of fields) {
    const children = getSurveyChildren(field);
    if (!children && isScalarSurveyLeaf(field)) {
      const titleLower = field.title.toLowerCase();
      if (keywords.some((kw) => titleLower.includes(kw.toLowerCase()))) {
        return getSurveyLeafRawValue(field);
      }
    }
    if (children) {
      const found = findFieldValue(children as AttributeField[], keywords);
      if (found !== null) return found;
    }
  }
  return null;
}

/** Exact-title leaf lookup. Used for cases where keyword search would match too broadly. */
export function findExactField(
  fields: AttributeField[],
  exactTitle: string
): string | number | boolean | null {
  for (const field of fields) {
    const children = getSurveyChildren(field);
    if (!children && isScalarSurveyLeaf(field) && field.title.toLowerCase() === exactTitle) {
      return getSurveyLeafRawValue(field);
    }
    if (children) {
      const found = findExactField(children as AttributeField[], exactTitle);
      if (found !== null) return found;
    }
  }
  return null;
}

/**
 * Default SF/ton ratios by ASHRAE climate zone.
 * Used when the attribute data doesn't include a specific ratio.
 */
const CLIMATE_ZONE_RATIOS: Record<string, number> = {
  "1": 200, "1a": 200,
  "2": 215, "2a": 215, "2b": 220,
  "3": 235, "3a": 235, "3b": 240, "3c": 260,
  "4": 265, "4a": 265, "4b": 270, "4c": 290,
  "5": 280, "5a": 280, "5b": 285, "5c": 295,
  "6": 320, "6a": 320, "6b": 325,
  "7": 340,
  "8": 360,
};

// Text-based climate zone names → SF per ton
const CLIMATE_ZONE_TEXT: Record<string, number> = {
  "northeast": 395,
  "midwest": 340,
  "southeast": 235,
  "south": 215,
  "southwest": 220,
  "west": 280,
  "northwest": 320,
  "pacific": 290,
};

export function getSfPerTonFromClimateZone(zoneRaw: string | null): number | null {
  if (!zoneRaw) return null;
  // Try numeric zone first, e.g. "Zone 3A" -> "3a"
  const match = zoneRaw.match(/(\d+[a-c]?)/i);
  if (match) {
    const key = match[1].toLowerCase();
    const ratio = CLIMATE_ZONE_RATIOS[key];
    if (ratio != null) return ratio;
  }
  // Fall back to text-based lookup, e.g. "Northeast"
  const textKey = zoneRaw.trim().toLowerCase();
  return CLIMATE_ZONE_TEXT[textKey] ?? null;
}

export function summariseNodeValues(
  values: Record<string, string | number | boolean | null>,
  conditionKeywords: string[],
  materialKeywords: string[],
  notesKeywords: string[]
): { condition: string | null; material: string | null; notes: string | null } {
  let condition: string | null = null;
  let material: string | null = null;
  const notesParts: string[] = [];

  for (const [key, val] of Object.entries(values)) {
    if (val === null || val === undefined || val === "") continue;
    const keyLower = key.toLowerCase();
    const valStr = String(val);

    if (!condition && conditionKeywords.some((kw) => keyLower.includes(kw))) {
      condition = valStr;
    } else if (!material && materialKeywords.some((kw) => keyLower.includes(kw))) {
      material = valStr;
    } else if (notesKeywords.some((kw) => keyLower.includes(kw))) {
      notesParts.push(valStr);
    } else if (!material && !condition) {
      // Heuristic: if we haven't found material or condition yet, keep collecting
      notesParts.push(valStr);
    }
  }

  return {
    condition,
    material,
    notes: notesParts.length > 0 ? notesParts.join(" • ") : null,
  };
}
